'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import MobilePartnerLayout from '@/components/MobilePartnerLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Lead {
  id: string;
  name: string;
  stage: string;
  kavling_name: string | null;
  created_at: string;
}

interface AddLeadModalProps {
  onClose: () => void;
  onSuccess: () => void;
  agentId: string | null;
  agentCode: string | null;
}

function AddLeadModal({ onClose, onSuccess, agentId, agentCode }: AddLeadModalProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [interest, setInterest] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const supabase = createClient();
      const { error: err } = await supabase.from('leads').insert({
        name: name.trim(),
        phone: phone.trim(),
        kavling_name: interest.trim() || null,
        stage: 'New',
        source: 'Lainnya',
        agent_id: agentId || null,
        agent_code: agentCode || null,
        privacy_consent: true,
      });
      if (err) throw err;
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e.message || 'Gagal menyimpan lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-t-3xl shadow-2xl pb-8">
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <span className="text-base font-bold text-foreground">Tambah Lead Baru</span>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 pt-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">{error}</div>
          )}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Nama Lengkap *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Contoh: Budi Santoso"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Nomor WhatsApp *</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="08123456789"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Minat Kavling</label>
            <input
              type="text"
              value={interest}
              onChange={(e) => setInterest(e.target.value)}
              placeholder="Contoh: Green Valley GV-A12"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <button
            type="submit"
            disabled={saving || !name.trim() || !phone.trim()}
            className="w-full py-3.5 bg-primary text-white rounded-xl font-bold text-sm disabled:opacity-60 active:scale-95 transition-all"
          >
            {saving ? 'Menyimpan...' : '✓ Simpan Lead'}
          </button>
        </form>
      </div>
    </div>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Baru saja';
  if (mins < 60) return `${mins} menit lalu`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} jam lalu`;
  return `${Math.floor(hrs / 24)} hari lalu`;
}

export default function MobileHomePage() {
  const { user, profile } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddLead, setShowAddLead] = useState(false);

  const supabase = createClient();

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const fetchData = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('leads')
        .select('id, name, stage, kavling_name, created_at')
        .eq('agent_id', user.id)
        .order('created_at', { ascending: false });
      if (data) setLeads(data as Lead[]);
      setLoading(false);
    };
    fetchData();

    const channel = supabase
      .channel('mobile_home_leads')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'leads', filter: `agent_id=eq.${user.id}` }, (payload) => {
        setLeads((prev) => [payload.new as Lead, ...prev]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const totalLeads = leads.length;
  const validLeads = leads.filter((l) => ['Contacted', 'Survey', 'Booking', 'Closing'].includes(l.stage)).length;
  const bookingLeads = leads.filter((l) => l.stage === 'Booking').length;
  const closingLeads = leads.filter((l) => l.stage === 'Closing').length;

  const targetLead = 60;
  const targetProgress = Math.min((totalLeads / targetLead) * 100, 100);
  const remaining = Math.max(targetLead - totalLeads, 0);

  const firstName = profile?.full_name?.split(' ')[0] || 'Partner';
  const recentActivities = leads.slice(0, 3).map((l) => ({
    id: l.id,
    text: l.stage === 'New' ? `Lead baru masuk` : `${l.name} diupdate ke ${l.stage}`,
    detail: l.kavling_name || 'Kavling',
    time: timeAgo(l.created_at),
  }));

  return (
    <MobilePartnerLayout showFloatingButton onFloatingButtonClick={() => setShowAddLead(true)}>
      {/* Header */}
      <div className="bg-primary px-5 pt-12 pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-white font-bold text-base">
                {firstName.slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-white/80 text-xs">Selamat datang 👋</p>
              <p className="text-white font-bold text-base leading-tight">Halo, {firstName}!</p>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-yellow-300 text-xs">⭐ Partner</span>
                {profile?.agent_code && (
                  <span className="text-white/60 text-xs">· {profile.agent_code}</span>
                )}
              </div>
            </div>
          </div>
          <button className="relative p-2 rounded-full bg-white/20 text-white">
            <Icon name="BellIcon" size={20} />
          </button>
        </div>
      </div>

      <div className="px-4 -mt-3 space-y-4 pb-4">
        {/* Performance Card */}
        <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Performa Saya</span>
            <span className="text-xs text-primary font-semibold">Total</span>
          </div>
          <div className="grid grid-cols-2 divide-x divide-y divide-border">
            {[
              { label: 'Lead', value: loading ? '—' : String(totalLeads), color: 'text-primary' },
              { label: 'Valid Lead', value: loading ? '—' : String(validLeads), color: 'text-secondary' },
              { label: 'Booking', value: loading ? '—' : String(bookingLeads), color: 'text-orange' },
              { label: 'Closing', value: loading ? '—' : String(closingLeads), color: 'text-accent' },
            ].map((item) => (
              <div key={item.label} className="px-4 py-3 text-center">
                <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <p className="text-sm font-bold text-foreground mb-2.5">Aksi Cepat</p>
          <div className="grid grid-cols-3 gap-3">
            <Link
              href="/partner/mobile/kavling"
              className="flex flex-col items-center gap-2 bg-primary/10 rounded-2xl p-4 active:scale-95 transition-all"
            >
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Icon name="ShareIcon" size={20} className="text-white" />
              </div>
              <span className="text-xs font-semibold text-primary text-center leading-tight">Share Kavling</span>
            </Link>
            <button
              onClick={() => setShowAddLead(true)}
              className="flex flex-col items-center gap-2 bg-secondary/10 rounded-2xl p-4 active:scale-95 transition-all"
            >
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                <Icon name="UserPlusIcon" size={20} className="text-white" />
              </div>
              <span className="text-xs font-semibold text-secondary text-center leading-tight">Tambah Lead</span>
            </button>
            <Link
              href="/partner/mobile/lead"
              className="flex flex-col items-center gap-2 bg-orange/10 rounded-2xl p-4 active:scale-95 transition-all"
            >
              <div className="w-10 h-10 rounded-xl bg-orange flex items-center justify-center">
                <Icon name="PhoneIcon" size={20} className="text-white" />
              </div>
              <span className="text-xs font-semibold text-orange text-center leading-tight">Follow Up</span>
            </Link>
          </div>
        </div>

        {/* Target Progress */}
        <div className="bg-card rounded-2xl border border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-base">🎯</span>
              <span className="text-sm font-bold text-foreground">Target Lead</span>
            </div>
            <Link href="/partner/mobile/lead" className="text-xs text-primary font-semibold">Lihat Detail</Link>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground font-medium">Lead</span>
              <span className="font-bold text-foreground">{totalLeads} / {targetLead}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
              <div
                className="h-2.5 rounded-full bg-primary transition-all duration-700"
                style={{ width: `${targetProgress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {remaining > 0 ? `${remaining} lead lagi untuk mencapai target` : '🎉 Target tercapai!'}
            </p>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-card rounded-2xl border border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-foreground">Aktivitas Terbaru</span>
            <Link href="/partner/mobile/lead" className="text-xs text-primary font-semibold">Lihat Semua</Link>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-muted flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-muted rounded w-3/4" />
                    <div className="h-2.5 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentActivities.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Belum ada aktivitas. Mulai tambah lead!</p>
          ) : (
            <div className="space-y-3">
              {recentActivities.map((act) => (
                <div key={act.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="CheckCircleIcon" size={16} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground leading-tight">{act.text}</p>
                    <p className="text-xs text-muted-foreground">{act.detail}</p>
                  </div>
                  <span className="text-xs text-muted-foreground flex-shrink-0">{act.time}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showAddLead && (
        <AddLeadModal
          onClose={() => setShowAddLead(false)}
          onSuccess={() => {}}
          agentId={user?.id || null}
          agentCode={profile?.agent_code || null}
        />
      )}
    </MobilePartnerLayout>
  );
}
